/**
 * AiReliabilityWidget — Widget Fiabilité IA (Phase 8).
 * Métriques agrégées tenant, non-sensibles RGPD.
 * Toggle période 7j / 30j / 90j.
 * Sparkline compact des dictations/structurations.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Info, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PERIODS = [
  { key: "7d", label: "7 j" },
  { key: "30d", label: "30 j" },
  { key: "90d", label: "90 j" },
];

function Sparkline({ series }) {
  if (!series || series.length === 0) return null;
  const w = 240;
  const h = 32;
  const max = Math.max(1, ...series.map((s) => s.dictations + s.structurations));
  const step = w / Math.max(1, series.length - 1);
  const pts = series
    .map((s, i) => `${i * step},${h - ((s.dictations + s.structurations) / max) * (h - 2) - 1}`)
    .join(" ");
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
         className="text-[#C4553B]" data-testid="ai-widget-sparkline">
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={pts} />
    </svg>
  );
}

function Metric({ label, value, hint, testid, unit = "" }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="rounded border border-slate-800 bg-slate-950/30 p-3" data-testid={testid}>
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-slate-500">
          <span>{label}</span>
          {hint && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 cursor-help text-slate-500" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs border-slate-700 bg-slate-800 text-slate-100">
                {hint}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="mt-1 text-2xl font-semibold text-slate-100 tabular-nums">
          {value}
          {unit && <span className="ml-1 text-sm text-slate-400">{unit}</span>}
        </div>
      </div>
    </TooltipProvider>
  );
}

export default function AiReliabilityWidget() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/dashboard/ai-reliability?period=${period}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period]);

  // Phase B2 — Empty state : masquer entièrement le widget si aucune
  // dictée n'a encore été traitée. Évite un bloc "zéros bruts" en pleine
  // hauteur qui laisse penser à un bug. La carte "Ouvrir l'assistant vocal"
  // en haut du dashboard reste (elle sert d'amorce).
  if (!loading && data && data.dictations && data.dictations.total === 0) {
    return null;
  }

  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="widget-ai-reliability">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-200">
            <Sparkles className="h-4 w-4 text-[#C4553B]" /> Fiabilité de l&apos;assistant IA
          </h3>
          <div className="flex gap-1 rounded-full border border-slate-800 bg-slate-950/30 p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] transition-colors ${
                  period === p.key
                    ? "bg-[#C4553B] text-white"
                    : "text-slate-400 hover:text-slate-200"
                }`}
                data-testid={`ai-widget-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </div>
        ) : !data ? (
          <div className="py-6 text-center text-xs text-slate-500">
            Métriques IA indisponibles.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Metric
                label="Dictées"
                value={data.dictations.total}
                testid="ai-widget-dictations"
                hint="Nombre total de dictées audio traitées par l'assistant vocal sur la période."
              />
              <Metric
                label="Structurations"
                value={data.structurations.total}
                testid="ai-widget-structurations"
                hint="Nombre de brouillons de devis structurés par l'assistant (via dictée ou texte libre)."
              />
              <Metric
                label="Match catalogue"
                value={data.structurations.catalog_match_pct}
                unit="%"
                testid="ai-widget-catalog-match"
                hint="Part des lignes structurées qui correspondent à un poste existant de votre catalogue. Plus le taux monte, plus l'assistant se cale sur votre bibliothèque."
              />
              <Metric
                label="Relances IA"
                value={data.reminders.ai_generation_pct}
                unit="%"
                testid="ai-widget-reminder-ai"
                hint="Part des relances (devis/factures) rédigées par l'IA. Les autres utilisent une formule pré-écrite de secours."
              />
            </div>
            <div className="mt-4">
              <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">
                Activité IA sur {data.period_days} jours
              </div>
              <Sparkline series={data.daily_series} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-[11px] text-slate-500 sm:grid-cols-4">
              <span>Durée dictée moy. : <b className="text-slate-300">{data.dictations.avg_duration_seconds}s</b></span>
              <span>Lignes générées : <b className="text-slate-300">{data.structurations.lines_generated}</b></span>
              <span>À vérifier : <b className="text-slate-300">{data.structurations.needs_review}</b></span>
              <span>Corrections auto. € : <b className="text-slate-300">{data.currency_fixes_global}</b></span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
